// Pure helpers for the developer Overview page: what needs attention, how a
// figure changed, and friendly labels.
import { relativeTime } from "../../lib/time";
import type { DeveloperOverview, Env } from "../../lib/types";

export type Severity = "bad" | "warn" | "info";

export interface Attention {
  id: string;
  severity: Severity;
  title: string;
  body: string;
  to?: string;
  cta?: string;
}

export const TXN_LABELS: Record<string, string> = {
  funding: "Funding",
  transfer: "Transfers",
  card_payment: "Card payments",
  card_refund: "Card refunds",
  fee: "Fees",
  interest: "Interest",
  reversal: "Reversals",
  adjustment: "Adjustments",
  loan_disbursement: "Loans paid out",
  loan_repayment: "Loan repayments",
};

export const DECLINE_LABELS: Record<string, string> = {
  insufficient_funds: "Not enough money",
  exceeds_daily_limit: "Over the daily limit",
  exceeds_transaction_limit: "Over the per-payment limit",
  card_blocked: "Card blocked",
  card_lost: "Card reported lost",
  card_stolen: "Card reported stolen",
  card_expired: "Card expired",
  card_closed: "Card closed",
  invalid_cvv: "Wrong CVV",
  invalid_expiry: "Wrong expiry date",
  account_frozen: "Account frozen",
  account_closed: "Account closed",
  merchant_inactive: "Shop suspended",
};

export const label = (map: Record<string, string>, key: string) =>
  map[key] ?? key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/** How this week compares with the week before. */
export function weekChange(now: number, before: number): { text: string; tone: "up" | "down" | "flat" } {
  if (now === 0 && before === 0) return { text: "No activity yet", tone: "flat" };
  if (before === 0) return { text: "New this week", tone: "up" };
  const pct = Math.round(((now - before) / before) * 100);
  if (pct === 0) return { text: "Same as the week before", tone: "flat" };
  return { text: `${pct > 0 ? "+" : ""}${pct}% on the week before`, tone: pct > 0 ? "up" : "down" };
}

const ENV_WORD: Record<Env, string> = { sandbox: "sandbox", production: "live" };

/** Things worth the developer's time, most urgent first. */
export function attention(o: DeveloperOverview, env: Env, now = Date.now()): Attention[] {
  const items: Attention[] = [];
  for (const e of o.webhooks.issues.filter((x) => x.environment === env)) {
    const where = `${e.game_name} · ${ENV_WORD[e.environment]}`;
    items.push(
      e.status === "disabled"
        ? {
            id: `ep-${e.id}`, severity: "bad", title: "A webhook endpoint is switched off",
            body: `${e.url} (${where}) gets no alerts${e.disabled_reason ? `: ${e.disabled_reason}` : ""}.`,
            to: `/dev/games/${e.game_id}/webhooks`, cta: "Fix the endpoint",
          }
        : {
            id: `ep-${e.id}`, severity: "warn", title: "Webhook deliveries are failing",
            body: `${e.url} (${where}) started failing ${relativeTime(e.failing_since, now)}. Endpoints that keep failing for 3 days are switched off.`,
            to: `/dev/games/${e.game_id}/webhooks`, cta: "Check deliveries",
          },
    );
  }
  if (o.webhooks.failed_24h > 0 && !items.some((i) => i.id.startsWith("ep-"))) {
    items.push({
      id: "deliveries-failed", severity: "warn", title: "Some webhook deliveries failed",
      body: `${o.webhooks.failed_24h} deliveries failed for good in the last 24 hours. They can be sent again from the event log.`,
    });
  }
  if (o.webhooks.pending > 100) {
    items.push({
      id: "deliveries-backlog", severity: "warn", title: "Webhook deliveries are queuing up",
      body: `${o.webhooks.pending} deliveries are waiting to be sent or retried.`,
    });
  }
  const { authorizations_7d: auths, declined_7d: declined, decline_codes_7d: codes } = o.cards;
  if (auths >= 10 && declined / auths >= 0.25) {
    const top = codes[0] ? `, most often "${label(DECLINE_LABELS, codes[0].code).toLowerCase()}"` : "";
    items.push({
      id: "declines", severity: "warn", title: "Many card payments are declined",
      body: `${Math.round((declined / auths) * 100)}% of card payments this week were declined${top}.`,
    });
  }
  for (const k of o.keys.issues.filter((x) => x.environment === env)) {
    const where = `${k.game_name} · ${ENV_WORD[k.environment]}`;
    const to = `/dev/games/${k.game_id}/keys${k.environment === "production" ? "?env=production" : ""}`;
    if (k.issue === "expiring") {
      items.push({ id: `key-${k.id}`, severity: "warn", title: `Key "${k.label}" expires ${relativeTime(k.expires_at, now)}`,
        body: `${where}. Issue a replacement and switch your server over before it stops working.`, to, cta: "Manage keys" });
    } else if (k.issue === "never_used") {
      items.push({ id: `key-${k.id}`, severity: "info", title: `Key "${k.label}" has never been used`,
        body: `${where}, created ${relativeTime(k.created_at, now)}. Revoke keys you don't need.`, to, cta: "Manage keys" });
    } else {
      items.push({ id: `key-${k.id}`, severity: "info", title: `Key "${k.label}" hasn't been used lately`,
        body: `${where}, last used ${relativeTime(k.last_used_at, now)}. Revoke it if it's no longer in use.`, to, cta: "Manage keys" });
    }
  }
  if (env === "production" && o.games.active > 0 && o.keys.active.production === 0) {
    const first = o.per_game.find((g) => g.status === "active");
    items.push({
      id: "go-live", severity: "info", title: "Nothing is live yet",
      body: "None of your games has a production key. When the sandbox works, create a production bank and key to go live.",
      to: first ? `/dev/games/${first.id}/keys?env=production` : undefined, cta: "Set up production",
    });
  }
  if (o.games.suspended > 0) {
    items.push({
      id: "suspended", severity: "info", title: `${o.games.suspended} ${o.games.suspended === 1 ? "game is" : "games are"} suspended`,
      body: "Suspended games refuse every API call until they're reactivated.", to: "/dev/games", cta: "Review games",
    });
  }
  const rank: Record<Severity, number> = { bad: 0, warn: 1, info: 2 };
  return items.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/** Live when there is anything live to show, else sandbox. */
export function defaultEnv(o: DeveloperOverview): Env {
  return o.keys.active.production > 0 || o.transactions_7d.production > 0 || o.player_accounts.production > 0 ? "production" : "sandbox";
}
